import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHouse } from '@fortawesome/pro-light-svg-icons'

import { digObject } from '@domain-group/javascript-utils'

import {
  Box, Button, Columns, LoadingModule, Tools,
} from '@domain-group/suit-theme'

import useAgencySelector from '@hooks/useAgencySelector'
import useCampaign, { generateUrls } from '@hooks/useCampaign'
import useCampaignAccounting from '@hooks/useCampaignAccounting'

import generateNavigationItems from
  '@sections/Client/packs/Accounting/components/AccountingNavigation/utils/generateNavigationItems'

import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'
import ReconciliationTotals from '@components/ReconciliationTotals'
import StatementHistory from '@components/StatementHistory'

import PageContext from '@contexts/pageContext'

import CampaignStatementBlankState from '../CampaignStatementBlankState'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'campaigns',
        requestOptions: { include_campaign_reconciliation_totals: true },
        secondaryTextKey: 'dates.created.date'
      },
      key: 'campaign_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        defaultSelectLabel: 'Show All',
        style,
      },
      key: 'itemType',
      label: 'Select Option',
      values: [
        { key: 'bill', label: 'Bill' },
        { key: 'campaign_balance', label: 'Campaign Balance' },
        { key: 'credit', label: 'Credit' },
        { key: 'invoice', label: 'Invoice' },
        { key: 'payment', label: 'Payment' },
      ],
    },
  ]
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  const { selectedAgencyId } = useAgencySelector()

  // @ts-expect-error TS2345
  const nav = generateNavigationItems(selectedAgencyId)
  const items = digObject(nav.sections[0], 'items')

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { campaign, loading: loadingCampaign } = useCampaign({ id: pageFilters.campaign_id })
  const { campaignEditDetailsUrl } = generateUrls(campaign, { prefixRoute: '' })

  const { campaign_id: selectedCampaignId } = pageFilters || {}

  const campaignAccountingPayload = useCampaignAccounting({
    agencyId: campaign.agency_id,
    campaignId: selectedCampaignId,
    filters: { ...pageFilters },
    performHttpRequests: !!selectedCampaignId,
  })

  const {
    callbacks, filteredStatementItems, hasFilteredStatementItems, loading: loadingAccounting,
  } = campaignAccountingPayload

  const loading = loadingAccounting || loadingCampaign

  return (
    <>
      <Helmet>
        <title>Campaign Statement | Realhub</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="campaign-statement"
        nestedNavigation
        tabBarItems={items}
        title="Campaign Statement"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.agencyManager.campaignStatement.banner" />

            <LoadingModule loading={loadingAccounting} times={3} />

            {!selectedCampaignId && (
              <CampaignStatementBlankState />
            )}

            {selectedCampaignId && !loading && (
              <StatementHistory
                callbacks={callbacks}
                campaign={campaign}
                hasFilteredStatementItems={hasFilteredStatementItems}
                // @ts-expect-error TS2322
                items={filteredStatementItems}
                title={campaign.title}
              />
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.agencyManager.campaignStatement.sidebar" />

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
              title="Filters"
            />

            {selectedCampaignId && (
              <Tools>
                <Button
                  // @ts-expect-error TS2322
                  as="a"
                  buttonStyle="secondaryUtility"
                  href={campaignEditDetailsUrl}
                  icon={<FontAwesomeIcon icon={faHouse} />}
                  size="medium"
                >
                  View Campaign
                </Button>
              </Tools>
            )}

            {selectedCampaignId && (
              <ReconciliationTotals
                campaign={campaign}
                displayKeys={[
                  'approved_order_total',
                  'campaign_item_total',
                  'credit_note_total',
                  'received_contribution_total',
                  'campaign_balance',
                ]}
              />
            )}
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
